'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import useRegisterStore from '@/stores/register';
import { useState, useCallback, useMemo, useActionState, startTransition, useEffect, useRef } from 'react';
import { toast } from '@/components/toast';
import { ArrowLeft, Search, Building2, TrendingUp, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Image from 'next/image';
import type { Bank } from '@/utils/bankImageMapper';
import { BankConnectionConfirmModal } from '@/components/BankConnectionConfirmModal';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useMediaQuery } from 'usehooks-ts';
import { Badge } from '@/components/ui/badge';
import { createItemAndGetAuthUrlAction, type CreateItemAndGetAuthUrlActionState, type CreateItemAndGetAuthUrlPayload } from '@/app/(auth)/actions';
import * as Sentry from '@sentry/nextjs';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import useIsWebview from '@/hooks/use-is-webview';
import openExternalLink from '@/utils/openExternalLink';
import { verifyNativeAuth } from '@/utils/checkNativeAuth';
import { isInMobileWebView } from '@/utils/sendNativeAuth';
import { openNativeOnboardingBankSelection } from '@/utils/nativeNavigation';
import { getAppVersionFromUserAgent, isAppVersionAtLeast } from '@/lib/utils/platform';
import { QRCodeCanvas } from 'qrcode.react';
import { v4 as uuidv4 } from 'uuid';
import { useSearchParams, useRouter } from 'next/navigation';
import { signIn as nextAuthSignIn } from 'next-auth/react';
import { Spinner } from '@/components/ui/spinner';
import { OPEN_FINANCE_UNAVAILABLE } from '@/lib/constants';
interface RegisterOnboardingBankSelectionStepProps {
  banks: Bank[];
  brokerages?: Bank[];
}

// Helper function to validate if URL is HTTP/HTTPS
const isValidHttpUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};
const RegisterOnboardingBankSelectionStep = ({
  banks,
  brokerages = []
}: RegisterOnboardingBankSelectionStepProps) => {
  const {
    previousStep,
    formData: registerData,
    userId,
    registrationToken
  } = useRegisterStore(['previousStep', 'formData', 'userId', 'registrationToken']);
  const analytics = useRudderStackAnalytics();
  const [isWebView] = useIsWebview();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const searchParams = useSearchParams();
  const router = useRouter();

  // Social auth users (Google/Apple) don't have a password
  const isSocialAuth = searchParams.get('skipPassword') === 'true';
  const [state, formAction] = useActionState<CreateItemAndGetAuthUrlActionState, CreateItemAndGetAuthUrlPayload>(createItemAndGetAuthUrlAction, {
    status: 'idle'
  });
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingStep, setLoadingStep] = useState<'idle' | 'saving' | 'connecting' | 'redirecting' | 'verifying_auth'>('idle');
  const hasShownSuccessToast = useRef(false);
  const hasTrackedPageView = useRef(false);
  const lastProcessedStateRef = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRedirectingRef = useRef(false);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  // QR Code modal states
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  // Tab state for banks/brokerages
  const [activeTab, setActiveTab] = useState<'banks' | 'brokerages'>('banks');
  const hasBrokerages = brokerages.length > 0;
  const [isSkippingMaintenance, setIsSkippingMaintenance] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  // When running inside a recent enough native WebView, this step hands the
  // bank-selection flow off to the native bank picker + sync screen. The
  // native side unmounts the WebView on receipt of the bridge message, so we
  // just need to avoid rendering the web picker behind the scenes.
  const [isNativeHandoff, setIsNativeHandoff] = useState(false);

  // Combine banks and brokerages for selection lookup
  const allConnectors = useMemo(() => {
    return [...banks, ...brokerages];
  }, [banks, brokerages]);
  const filteredBanks = useMemo(() => {
    const sourceList = activeTab === 'banks' ? banks : brokerages;
    if (!searchQuery) return sourceList;
    const query = searchQuery.toLowerCase().trim();
    return sourceList.filter(bank => bank.name.toLowerCase().includes(query));
  }, [banks, brokerages, activeTab, searchQuery]);
  const selectedBank = useMemo(() => {
    if (!selectedBankId) return null;
    return allConnectors.find(bank => String(bank.id) === selectedBankId) || null;
  }, [allConnectors, selectedBankId]);
  const handleBankSelect = useCallback((bankId: string) => {
    setSelectedBankId(bankId);
    // Don't reset lastProcessedStateRef here - it will cause the old error to show again
  }, []);
  const handleNext = useCallback(() => {
    if (!selectedBankId) {
      toast({
        type: 'error',
        description: 'Selecione um banco para continuar'
      });
      return;
    }

    // Don't reset lastProcessedStateRef here either - wait until we actually submit
    hasShownSuccessToast.current = false;
    setIsModalOpen(true);
  }, [selectedBankId, state.status]);
  const handleConfirm = useCallback(async () => {
    if (!selectedBankId || !selectedBank || !registerData.document) {
      Sentry.withScope(scope => {
        scope.setTag('context', 'onboarding_error');
        scope.setLevel('error');
        Sentry.captureException({
          message: 'Erro ao conectar com o banco',
          extra: {
            selectedBankId,
            selectedBank,
            document: registerData.document
          }
        });
      });
      return;
    }

    // Social auth users (Google/Apple) don't have a password, so skip password check for them
    if (!registerData.email) {
      Sentry.withScope(scope => {
        scope.setTag('context', 'onboarding_error_login');
        scope.setLevel('error');
        Sentry.captureException({
          message: 'Erro no form, sem credenciais para login',
          extra: {
            hasEmail: !!registerData.email,
            hasPassword: !!registerData.password,
            isSocialAuth
          }
        });
      });
      return;
    }
    if (!userId || !registrationToken) {
      toast({
        type: 'error',
        description: 'Usuário não encontrado. Por favor, faça login novamente.'
      });
      return;
    }
    setIsSaving(true);
    setLoadingStep('connecting');

    // Reset processing flag - we're starting a new attempt
    lastProcessedStateRef.current = '';
    const tempStateId = uuidv4();
    startTransition(() => {
      formAction({
        connectorId: selectedBankId!,
        document: registerData.document!,
        // Signed token is the verified userId source for the pre-session
        // WebView <2.0 path; web users are already session-authenticated so
        // the action uses `auth()` first and only falls back to this (#4126).
        registrationToken,
        stateId: tempStateId
      });
    });
  }, [selectedBankId, selectedBank, userId, registrationToken, registerData, isSocialAuth]);
  const showQrCodeFallback = useCallback((url: string, itemId?: string, reason?: string) => {
    // Only show QR code if we're still in redirecting state
    if (!isRedirectingRef.current) return;
    isRedirectingRef.current = false;
    setLoadingStep('idle');
    setIsSaving(false);
    setIsModalOpen(false);

    // Only show QR code on desktop - on mobile, just reset and let user choose another bank
    if (isDesktop) {
      setQrCodeUrl(url);
      setShowQrModal(true);
      analytics?.track?.('Bank Connection Flow', {
        step: 'qr_code_fallback',
        platform: isWebView ? 'app' : 'web',
        source: 'onboarding',
        item_id: itemId,
        reason: reason || 'redirect_failed'
      });
      toast({
        type: 'info',
        description: 'Não foi possível abrir o link automaticamente. Escaneie o QR code para continuar.'
      });
    } else {
      // On mobile, just show error and reset
      analytics?.track?.('Bank Connection Flow', {
        step: 'connection_failed_mobile',
        platform: isWebView ? 'app' : 'web',
        source: 'onboarding',
        item_id: itemId,
        reason: reason || 'redirect_failed'
      });
      toast({
        type: 'error',
        description: 'Não foi possível conectar com este banco. Por favor, tente outro banco ou verifique se o aplicativo está instalado.'
      });
    }
  }, [analytics, isWebView, isDesktop]);
  const resetToIdle = useCallback(() => {
    setIsSaving(false);
    setLoadingStep('idle');
    setIsModalOpen(false);
    hasShownSuccessToast.current = false;
    isRedirectingRef.current = false;

    // Clear timeouts if they exist
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    // Remove visibility change listener if it exists
    if (typeof window !== 'undefined' && 'visibilityState' in window.document && visibilityHandlerRef.current) {
      window.document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      visibilityHandlerRef.current = null;
    }
  }, []);

  /**
   * Verify native auth and open external link
   * For WebView: checks native auth status before proceeding
   * For web browser: opens link directly
   *
   * Note: During onboarding, we skip strict userId verification since we just completed
   * registration and sent native auth tokens. The auth is guaranteed to be valid.
   */
  const openExternalLinkWithAuthCheck = useCallback(async (url: string, itemId?: string): Promise<boolean> => {
    // If we're in a mobile WebView with native auth support, verify auth first
    // Old app versions (< 1.0.14) don't support CHECK_NATIVE_AUTH messages, so skip for them
    const appVersion = getAppVersionFromUserAgent();
    const supportsNativeAuth = appVersion && isAppVersionAtLeast('1.0.14');
    if (isInMobileWebView() && supportsNativeAuth) {
      setLoadingStep('verifying_auth');
      analytics?.track?.('Bank Connection Flow', {
        step: 'verifying_native_auth',
        platform: 'app',
        source: 'onboarding',
        item_id: itemId,
        user_id: userId
      });

      // During onboarding, we don't pass userId to avoid strict matching issues
      // since we just completed registration and sent native auth tokens.
      // The native app is guaranteed to be authenticated at this point.
      const isAuthValid = await verifyNativeAuth(undefined, 10000); // 10s timeout, no userId check

      if (!isAuthValid) {
        console.error('[BankSelection] Native auth verification failed');
        analytics?.track?.('Bank Connection Flow', {
          step: 'native_auth_verification_failed',
          platform: 'app',
          source: 'onboarding',
          item_id: itemId,
          user_id: userId
        });
        Sentry.withScope(scope => {
          scope.setTag('context', 'native_auth_verification_failed');
          scope.setLevel('error');
          scope.setExtra('userId', userId);
          scope.setExtra('itemId', itemId);
          Sentry.captureMessage('Native auth verification failed before bank connection');
        });
        toast({
          type: 'error',
          description: 'Erro de autenticação. Por favor, feche o aplicativo e tente novamente.'
        });
        resetToIdle();
        return false;
      }
      analytics?.track?.('Bank Connection Flow', {
        step: 'native_auth_verified',
        platform: 'app',
        source: 'onboarding',
        item_id: itemId,
        user_id: userId
      });
    }

    // Auth verified (or not in WebView), proceed with opening the link
    setLoadingStep('redirecting');
    isRedirectingRef.current = true;
    try {
      openExternalLink(url, true);
      return true;
    } catch (error) {
      console.error('[BankSelection] Failed to open external link:', error);
      return false;
    }
  }, [analytics, userId, resetToIdle]);

  // Process state changes from the action
  useEffect(() => {
    // Only process actual state changes, not the initial idle state
    if (state.status === 'idle' || state.status === 'in_progress') {
      return;
    }

    // Create unique key for this state to prevent duplicate processing
    const stateKey = `${state.status}-${state.authUrl || ''}-${state.itemId || ''}`;

    // Skip if we've already processed this exact state
    if (lastProcessedStateRef.current === stateKey) {
      return;
    }
    if (state.status === 'failed') {
      // Mark this state as processed
      lastProcessedStateRef.current = stateKey;
      toast({
        type: 'error',
        description: 'Erro ao conectar com o banco. Tente novamente.'
      });

      // Reset to idle state
      resetToIdle();
      return;
    }
    if (state.status === 'success' && state.authUrl && !hasShownSuccessToast.current) {
      // Mark this state as processed
      lastProcessedStateRef.current = stateKey;
      hasShownSuccessToast.current = true;

      // Clear timeout on success
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Check if the URL is a valid HTTP/HTTPS link
      if (isValidHttpUrl(state.authUrl)) {
        // Valid web URL - attempt to redirect with fallback to QR code
        toast({
          type: 'success',
          description: 'Link de autenticação gerado com sucesso. Estamos redirecionando você.'
        });
        analytics?.track?.('Bank Connection Flow', {
          step: 'bank_redirect',
          platform: isWebView ? 'app' : 'web',
          source: 'onboarding',
          item_id: state.itemId
        });

        // Listen for visibility change to detect if user returns to the page
        const handleVisibilityChange = () => {
          if (typeof window !== 'undefined' && 'visibilityState' in window.document && window.document.visibilityState === 'visible' && isRedirectingRef.current) {
            // User came back to the page, redirect likely failed
            console.warn('User returned to page - showing QR code fallback');
            showQrCodeFallback(state.authUrl!, state.itemId, 'user_returned');
            if (visibilityHandlerRef.current) {
              window.document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
              visibilityHandlerRef.current = null;
            }
          }
        };

        // Store handler reference for cleanup
        if (typeof window !== 'undefined' && 'visibilityState' in window.document) {
          visibilityHandlerRef.current = handleVisibilityChange;
          window.document.addEventListener('visibilitychange', handleVisibilityChange);
        }

        // Attempt to open the link with auth verification for WebView
        openExternalLinkWithAuthCheck(state.authUrl, state.itemId).then(success => {
          if (!success) {
            // Auth check failed or link open failed
            if (typeof window !== 'undefined' && 'visibilityState' in window.document && visibilityHandlerRef.current) {
              window.document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
              visibilityHandlerRef.current = null;
            }
            // If in WebView and auth failed, resetToIdle was already called
            // For sync errors, show QR code fallback
            if (!isInMobileWebView()) {
              showQrCodeFallback(state.authUrl!, state.itemId, 'sync_error');
            }
          }
        }).catch(error => {
          console.error('Failed to open external link:', error);
          if (typeof window !== 'undefined' && 'visibilityState' in window.document && visibilityHandlerRef.current) {
            window.document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
            visibilityHandlerRef.current = null;
          }
          showQrCodeFallback(state.authUrl!, state.itemId, 'async_error');
        });
      } else {
        // Non-HTTP URL (e.g., bancointer://...) - show QR code
        setLoadingStep('idle');
        setIsSaving(false);
        setIsModalOpen(false);
        setQrCodeUrl(state.authUrl);
        setShowQrModal(true);
        analytics?.track?.('Bank Connection Flow', {
          step: 'qr_code_shown',
          platform: isWebView ? 'app' : 'web',
          source: 'onboarding',
          item_id: state.itemId,
          url_scheme: state.authUrl.split('://')[0]
        });
        toast({
          type: 'info',
          description: 'Escaneie o QR code com seu celular para continuar a autenticação.'
        });
      }
    }
  }, [analytics, isWebView, state.status, state.authUrl, state.itemId, resetToIdle, showQrCodeFallback, openExternalLinkWithAuthCheck]);
  const handleCloseModal = useCallback(() => {
    // Only allow closing if not loading/redirecting
    if (loadingStep !== 'idle') {
      return;
    }
    setIsModalOpen(false);
  }, [loadingStep]);
  const handleCloseQrModal = useCallback(() => {
    setShowQrModal(false);
    setQrCodeUrl(null);
    hasShownSuccessToast.current = false;
  }, []);

  // Timeout effect: 1 minute timeout for link generation
  useEffect(() => {
    if (loadingStep === 'connecting' || loadingStep === 'verifying_auth') {
      // Start 1 minute timeout
      timeoutRef.current = setTimeout(() => {
        toast({
          type: 'error',
          description: 'Não foi possível gerar o link de autenticação. Por favor, tente novamente ou selecione outro banco.'
        });
        analytics?.track?.('Bank Connection Flow', {
          step: 'connection_timeout',
          platform: isWebView ? 'app' : 'web',
          source: 'onboarding',
          bank_id: selectedBankId
        });
        resetToIdle();
      }, 60000); // 60 seconds
    }

    // Cleanup timeout on unmount or when loadingStep changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [loadingStep, analytics, isWebView, selectedBankId, resetToIdle]);

  // Native bank-selection handoff.
  //
  // When the app is a recent native WebView (2.0+), skip the web bank
  // picker entirely and ask the native host to route into the native
  // SelectBankScreen → sync flow. Older versions (or desktop web) fall
  // through to the normal web picker.
  useEffect(() => {
    const handedOff = openNativeOnboardingBankSelection();
    if (!handedOff) return;
    setIsNativeHandoff(true);
    analytics?.track?.('Bank Connection Flow', {
      step: 'native_handoff',
      platform: 'app',
      source: 'onboarding'
    });
    // Run once on mount — the effect only triggers for recent native
    // WebViews, and we rely on the native host to unmount us.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!analytics || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    analytics.track('Register Step', {
      step: 'onboarding_bank_selection',
      platform: isWebView ? 'app' : 'web'
    });
    analytics.track('Bank Connection Flow', {
      step: 'bank_selection',
      platform: isWebView ? 'app' : 'web',
      source: 'onboarding'
    });
  }, [analytics, isWebView]);

  // Check if bank connection is in maintenance mode
  useEffect(() => {
    let cancelled = false;
    const checkMaintenance = async () => {
      try {
        const res = await fetch('/api/maintenance-status');
        const data = await res.json();
        if (!cancelled && data.maintenance) {
          setIsMaintenanceMode(true);
          analytics?.track('Bank Connection Unavailable', {
            source: 'onboarding_warning'
          });
        }
      } catch {
        // If check fails, let user proceed normally
      }
    };
    checkMaintenance();
    return () => {
      cancelled = true;
    };
  }, [analytics]);
  const handleSkipMaintenance = async () => {
    setIsSkippingMaintenance(true);

    // Sign in if user has credentials (non-social auth)
    if (registerData.email && registerData.password && !isSocialAuth) {
      await nextAuthSignIn('credentials', {
        email: registerData.email.toLowerCase(),
        password: registerData.password,
        redirect: false
      }).catch(() => {});
    }
    router.push('/chat');
  };

  // Cleanup effect: ensure all timeouts and listeners are removed on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      if (typeof window !== 'undefined' && 'visibilityState' in window.document && visibilityHandlerRef.current) {
        window.document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      }
    };
  }, []);
  if (isNativeHandoff) {
    // Waiting for the native host to unmount us after router.replace.
    // Kept deliberately minimal — it is visible only for a frame or two.
    return <div className="min-h-screen w-full flex flex-col items-center justify-center gap-4 px-6">
        <Spinner size="lg" spinnerClassName="bg-primary" />
        <p className="text-muted-foreground text-sm">Preparando conexão...</p>
      </div>;
  }
  if (isSkippingMaintenance) {
    return <div className="min-h-screen w-full flex flex-col items-center justify-center gap-4 px-6">
        <Spinner size="lg" spinnerClassName="bg-primary" />
        <p className="text-muted-foreground text-sm">
          Finalizando seu cadastro...
        </p>
      </div>;
  }
  return <div className="min-h-screen mt-10 w-full flex flex-col">
      <div className="w-full flex-1 flex flex-col px-6 md:py-10">
        <div className="flex-1 flex flex-col justify-start md:justify-start gap-6 max-w-xl mx-auto w-full">
          <div className="flex flex-1 flex-col gap-6">
            <button type="button" disabled={isSaving} onClick={previousStep} className="flex items-center md:hidden gap-2 text-primary hover:text-primary/80 transition-colors w-fit">
              <ArrowLeft className="size-6" />
            </button>

            <div className="flex flex-col gap-2 md:text-center">
              <h2 className="text-primary text-2xl lg:text-3xl font-semibold">
                {hasBrokerages ? 'Selecione sua instituição' : 'Selecione seu banco'}
              </h2>

              <span className="text-primary/70 text-sm">
                {hasBrokerages ? 'Escolha seu banco ou corretora para conectar' : 'Escolha sua principal conta para conectar'}
              </span>
            </div>

            {isMaintenanceMode && <div className="flex flex-col gap-3 rounded-xl border border-yellow-500/20 bg-yellow-500/10 px-4 py-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-5 text-yellow-500 shrink-0" />
                  <p className="text-sm font-medium">
                    {OPEN_FINANCE_UNAVAILABLE.toastTitle}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {OPEN_FINANCE_UNAVAILABLE.toastDescription}
                </p>
                <Button variant="outline" size="sm" onClick={handleSkipMaintenance} disabled={isSkippingMaintenance} className="w-fit">
                  {isSkippingMaintenance ? 'Finalizando...' : 'Pular e ir para o chat'}
                </Button>
              </div>}

            {hasBrokerages && <Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'banks' | 'brokerages')} className="w-full">
                <TabsList className="w-full grid grid-cols-2 h-12">
                  <TabsTrigger value="banks" className="flex items-center gap-2">
                    <Building2 className="size-4" />
                    Bancos
                  </TabsTrigger>
                  <TabsTrigger value="brokerages" className="flex items-center gap-2">
                    <TrendingUp className="size-4" />
                    Corretoras
                  </TabsTrigger>
                </TabsList>
              </Tabs>}

            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-primary/40" />
              <Input type="text" placeholder={activeTab === 'banks' ? 'Buscar bancos' : 'Buscar corretoras'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-12 pr-4 py-6 rounded-2xl border-primary/20 dark:border-white/20 bg-transparent text-primary placeholder:text-primary/40" inputSize="xl" rounded="2xl" />
            </div>

            <div className="space-y-0 scrollbar-hide overflow-y-auto max-h-[calc(100lvh-350px)] pb-28 md:max-h-[450px]">
              {!filteredBanks.length ? <div className="text-center py-8">
                  <p className="text-primary/60">
                    {activeTab === 'banks' ? 'Nenhum banco encontrado' : 'Nenhuma corretora encontrada'}
                  </p>
                </div> : filteredBanks.map(bank => {
              const isSelected = selectedBankId === String(bank.id);
              return <button key={bank.id} type="button" onClick={() => handleBankSelect(String(bank.id))} disabled={isSaving} className={cn('w-full px-4 py-4 transition-all duration-200', 'flex items-center gap-4 text-left', 'border-b border-primary/10 dark:border-white/10', 'hover:bg-primary/5 dark:hover:bg-white/5', isSelected && 'bg-primary/5 dark:bg-white/5', isSaving && 'opacity-50 cursor-not-allowed')}>
                      <div className="relative size-11 border-2 border-border rounded-xl overflow-hidden flex justify-center items-center">
                        <Image src={bank.image || '/images/banks/banco-do-brasil.svg'} alt={bank.name} width={44} height={44} quality={100} className="object-cover" />
                      </div>

                      <div className="flex-1 flex justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-primary">
                            {bank.name}
                          </span>
                          {bank.isBrokerage && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                              Corretora
                            </span>}
                        </div>

                        <div className="flex items-center gap-2">
                          {bank.health?.stage === 'BETA' && <Badge variant="unstable" className="w-fit text-[10px] px-2 py-0">
                              Beta
                            </Badge>}
                        </div>
                      </div>

                      <div className={cn('size-5 rounded-full border-2 flex items-center justify-center transition-all', isSelected ? 'border-brand-bright-green bg-brand-bright-green' : 'border-primary/30 dark:border-white/30')}>
                        {isSelected && <div className="size-2 rounded-full bg-black" />}
                      </div>
                    </button>;
            })}
            </div>
          </div>

          <div className="w-full absolute left-0 py-10 bg-background/10 backdrop-blur-xl bottom-0 md:mt-4 flex justify-center">
            <Button type="button" rounded="full" className="font-semibold md:max-w-[240px] w-full mx-4" onClick={handleNext} size="xl" disabled={!selectedBankId || isSaving}>
              Confirmar
            </Button>
          </div>
        </div>
      </div>

      <BankConnectionConfirmModal isOpen={isModalOpen} onClose={handleCloseModal} onConfirm={handleConfirm} selectedBank={selectedBank} isSaving={isSaving} loadingStep={loadingStep} closeable={loadingStep === 'idle'} />

      {/* QR Code Modal */}
      {isDesktop ? <Dialog open={showQrModal} onOpenChange={handleCloseQrModal}>
          <DialogContent className="sm:max-w-[440px] p-6">
            <DialogTitle className="hidden" />
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-primary text-xl font-semibold">
                  Escaneie o QR Code
                </h2>
                <p className="text-primary/70 text-sm">
                  Use seu celular para escanear o código e continuar a
                  autenticação no aplicativo do banco
                </p>
              </div>

              <div className="flex justify-center items-center p-6 bg-primary/5 dark:bg-white/5 rounded-2xl">
                <QRCodeCanvas value={qrCodeUrl || ''} size={200} />
              </div>

              <div className="flex flex-col gap-2 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-primary/80 text-sm">
                  <strong>Importante:</strong> Certifique-se de ter o aplicativo
                  do banco instalado no seu celular antes de escanear o código
                </p>
              </div>

              <Button type="button" rounded="full" className="font-semibold w-full" onClick={handleCloseQrModal} size="xl">
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog> : <Sheet open={showQrModal} onOpenChange={handleCloseQrModal}>
          <SheetContent side="bottom" className="rounded-t-3xl p-6" title="Escaneie o QR Code" description="Use seu celular para continuar">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-primary text-xl font-semibold">
                  Escaneie o QR Code
                </h2>
                <p className="text-primary/70 text-sm">
                  Use seu celular para escanear o código e continuar a
                  autenticação no aplicativo do banco
                </p>
              </div>

              <div className="flex justify-center items-center p-6 bg-primary/5 dark:bg-white/5 rounded-2xl">
                <QRCodeCanvas value={qrCodeUrl || ''} size={240} />
              </div>

              <div className="flex flex-col gap-2 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-primary/80 text-sm">
                  <strong>Importante:</strong> Certifique-se de ter o aplicativo
                  do banco instalado no seu celular antes de escanear o código
                </p>
              </div>

              <Button type="button" rounded="full" className="font-semibold w-full" onClick={handleCloseQrModal} size="xl">
                Fechar
              </Button>
            </div>
          </SheetContent>
        </Sheet>}
    </div>;
};
export default RegisterOnboardingBankSelectionStep;